import React, { useMemo, useRef, useState } from 'react';
import {
  BhiDialog,
  BorderedTabs,
  CenteredDisplayInput,
  DashedFrame,
  DialogCloseButton,
  FloatingIconButton,
  InsetPanel,
} from 'src/UIKit';
import {
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  Divider,
  Box,
  Stack,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  BorderColorOutlined,
  Close as CloseIcon,
  CloudUploadOutlined,
  DeleteOutline,
  TextFieldsOutlined,
} from '@mui/icons-material';
import { useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import { AdminCompany } from 'src/modules/companies/types';
import { usePostAdminCompanyLoaPreviewApi, useAddAdminCompanyLoaApi } from 'src/modules/companies/api';
import { CompanyDocument, SignLoaPayload } from 'src/modules/companies/types';
import { SIGNATURE_STYLE_OPTIONS, spreadDialogActionsSx } from 'src/modules/companies/helpers';
import { DocumentPreviewModal } from '../shared/components/DocumentPreviewModal';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import toast from 'react-hot-toast';

// Canvas drawing needs a concrete color string, not a theme path.
const SIGNATURE_INK = grey[900];

type SignLoaDialogProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  company?: AdminCompany;
};

const SignLoaDialog: React.FC<SignLoaDialogProps> = ({ open, onClose, onSuccess, company }) => {
  const [agreeToSign, setAgreeToSign] = useState<boolean>(false);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [previewDocument, setPreviewDocument] = useState<CompanyDocument | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const loaPreviewApi = usePostAdminCompanyLoaPreviewApi();
  const addLoaApi = useAddAdminCompanyLoaApi();
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const [formValues, setFormValues] = useState({
    customer: '',
    address: '',
    carrierName: '',
    carrierNumber: '',
    phoneNumber: '',
    phone: '',
  });
  const [initialFormValues, setInitialFormValues] = useState({
    customer: '',
    address: '',
    carrierName: '',
    carrierNumber: '',
    phoneNumber: '',
    phone: '',
  });
  const [typedSignature, setTypedSignature] = useState('');
  const [selectedSignatureStyle, setSelectedSignatureStyle] = useState(
    SIGNATURE_STYLE_OPTIONS[0]?.id ?? 'script'
  );
  const [signatureMode, setSignatureMode] = useState<'draw' | 'type' | 'upload'>('draw');
  const currentSignatureStyle = useMemo(
    () =>
      SIGNATURE_STYLE_OPTIONS.find((option) => option.id === selectedSignatureStyle) ??
      SIGNATURE_STYLE_OPTIONS[0],
    [selectedSignatureStyle]
  );
  const hasTypedSignature = useMemo(() => typedSignature.trim().length > 0, [typedSignature]);

  const handleChange =
    (field: keyof typeof formValues) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleSignatureStyleChange = (_event: React.SyntheticEvent, newStyleId: string) => {
    setSelectedSignatureStyle(newStyleId);
  };

  const handleSignatureModeChange = (_event: React.SyntheticEvent, newMode: 'draw' | 'type' | 'upload') => {
    setSignatureMode(newMode);
    clearSignature();
    // Ensure canvas dimensions realign with the new layout after the tab switch
    setTimeout(resizeCanvasForDpr, 0);
  };

  const allFieldsFilled = useMemo(() => {
    const f = formValues;
    return (
      f.customer.trim() !== '' &&
      f.address.trim() !== '' &&
      f.phoneNumber.trim() !== '' &&
      f.phone.trim() !== ''
    );  
  }, [formValues]);



  // Signature pad
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef<boolean>(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const [signatureProvided, setSignatureProvided] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const typedSignatureDebounceRef = useRef<number | null>(null);

  const canSubmit = useMemo(() => {
    return agreeToSign && signatureProvided && allFieldsFilled;
  }, [agreeToSign, signatureProvided, allFieldsFilled]);

  const hasEnteredData = useMemo(() => {
    const f = formValues;
    const i = initialFormValues;
    return (
      f.customer.trim() !== i.customer.trim() ||
      f.address.trim() !== i.address.trim() ||
      f.phoneNumber.trim() !== i.phoneNumber.trim() ||
      f.phone.trim() !== i.phone.trim() ||
      f.carrierName.trim() !== i.carrierName.trim() ||
      f.carrierNumber.trim() !== i.carrierNumber.trim() ||
      typedSignature.trim() !== '' ||
      signatureProvided ||
      agreeToSign ||
      f.phoneNumber.trim() !== i.phoneNumber.trim()
    );
  }, [formValues, initialFormValues, typedSignature, signatureProvided, agreeToSign]);

  const handleClose = () => {
    if (hasEnteredData) {
      openDialog({
        title: 'Unsaved Changes',
        content: 'You have entered information that will be lost if you close this dialog. Are you sure you want to continue?',
        onConfirm: onClose,
        confirmButtonText: 'Close',
      });
    } else {
      onClose();
    }
  };

  const getCanvasContext = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    return ctx;
  };

  const resizeCanvasForDpr = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth;
    const cssHeight = canvas.clientHeight;
    canvas.width = Math.max(1, Math.floor(cssWidth * dpr));
    canvas.height = Math.max(1, Math.floor(cssHeight * dpr));
    const ctx = getCanvasContext();
    if (ctx) {
      // Reset any previous scaling to avoid compounded transforms when switching modes
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, cssWidth, cssHeight);
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = SIGNATURE_INK;
    }
  };

  React.useEffect(() => {
    if (open) {
      // Delay to allow layout to apply size
      setTimeout(resizeCanvasForDpr, 0);
      window.addEventListener('resize', resizeCanvasForDpr);
      return () => window.removeEventListener('resize', resizeCanvasForDpr);
    }
  }, [open]);

  // Re-run resize when signature mode changes so canvas matches the updated layout (e.g., tab rows/height)
  React.useEffect(() => {
    if (!open) return;
    requestAnimationFrame(resizeCanvasForDpr);
  }, [signatureMode, open]);

  const getCanvasPoint = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    return { x, y };
  };

  const beginStroke = (x: number, y: number) => {
    const ctx = getCanvasContext();
    if (!ctx) return;
    isDrawingRef.current = true;
    lastPointRef.current = { x, y };
    setSignatureProvided(true);
  };

  const drawStroke = (x: number, y: number) => {
    const ctx = getCanvasContext();
    if (!ctx || !isDrawingRef.current || !lastPointRef.current) return;
    const last = lastPointRef.current;
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastPointRef.current = { x, y };
  };

  const endStroke = () => {
    isDrawingRef.current = false;
    lastPointRef.current = null;
  };

  const onMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const p = getCanvasPoint(e.clientX, e.clientY);
    beginStroke(p.x, p.y);
  };
  const onMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) return;
    const p = getCanvasPoint(e.clientX, e.clientY);
    drawStroke(p.x, p.y);
  };
  const onMouseUp = () => endStroke();
  const onMouseLeave = () => endStroke();

  const onTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length > 0) {
      const t = e.touches[0];
      const p = getCanvasPoint(t.clientX, t.clientY);
      beginStroke(p.x, p.y);
    }
  };
  const onTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length > 0 && isDrawingRef.current) {
      const t = e.touches[0];
      const p = getCanvasPoint(t.clientX, t.clientY);
      drawStroke(p.x, p.y);
    }
  };
  const onTouchEnd = () => endStroke();

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = getCanvasContext();
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Refill background and reset styles
    resizeCanvasForDpr();
    setTypedSignature('');
    setSignatureProvided(false);
  };

  const uploadSignatureImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        const ctx = getCanvasContext();
        if (!canvas || !ctx) return;

        const cssWidth = canvas.clientWidth;
        const cssHeight = canvas.clientHeight;

        // Extract bounding box of non-white/non-transparent pixels to avoid huge white margins
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = img.width;
        tempCanvas.height = img.height;
        const tctx = tempCanvas.getContext('2d');
        if (!tctx) return;
        tctx.drawImage(img, 0, 0);
        const { data, width: tw, height: th } = tctx.getImageData(0, 0, img.width, img.height);

        const isPixelVisible = (i: number) => {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
          if (a > 10) return true;
          return r < 245 || g < 245 || b < 245;
        };

        let minX = tw;
        let minY = th;
        let maxX = -1;
        let maxY = -1;

        for (let y = 0; y < th; y += 1) {
          for (let x = 0; x < tw; x += 1) {
            const idx = (y * tw + x) * 4;
            if (isPixelVisible(idx)) {
              if (x < minX) minX = x;
              if (y < minY) minY = y;
              if (x > maxX) maxX = x;
              if (y > maxY) maxY = y;
            }
          }
        }

        const hasContent = maxX >= minX && maxY >= minY;
        const contentPadding = 8;
        const bboxWidth = hasContent ? maxX - minX + 1 : tw;
        const bboxHeight = hasContent ? maxY - minY + 1 : th;
        const srcX = hasContent ? Math.max(0, minX - contentPadding) : 0;
        const srcY = hasContent ? Math.max(0, minY - contentPadding) : 0;
        const srcW = hasContent
          ? Math.min(tw - srcX, bboxWidth + contentPadding * 2)
          : tw;
        const srcH = hasContent
          ? Math.min(th - srcY, bboxHeight + contentPadding * 2)
          : th;

        // Clear background then image fitted with a small margin
        ctx.clearRect(0, 0, cssWidth, cssHeight);

        const targetPadding = Math.max(8, Math.floor(Math.min(cssWidth, cssHeight) * 0.03));
        const availableW = Math.max(1, cssWidth - targetPadding * 2);
        const availableH = Math.max(1, cssHeight - targetPadding * 2);

        const scale = Math.min(availableW / srcW, availableH / srcH, 1);
        const w = srcW * scale;
        const h = srcH * scale;
        const x = (cssWidth - w) / 2;
        const y = (cssHeight - h) / 2;

        ctx.drawImage(tempCanvas, srcX, srcY, srcW, srcH, x, y, w, h);
        setSignatureProvided(true);
      };
      img.src = typeof reader.result === 'string' ? reader.result : '';
    };
    reader.readAsDataURL(file);
    // reset input so the same file can be re-selected
    e.currentTarget.value = '';
  };

  const openUploadDialog = () => {
    fileInputRef.current?.click();
  };

  const applyTypedSignatureToCanvas = () => {
    const text = typedSignature.trim();
    const canvas = canvasRef.current;
    const style = currentSignatureStyle;

    if (!canvas || !style) {
      return;
    }

    resizeCanvasForDpr();
    const ctx = getCanvasContext();
    if (!ctx) {
      return;
    }

    const cssWidth = canvas.clientWidth;
    const cssHeight = canvas.clientHeight;

    ctx.clearRect(0, 0, cssWidth, cssHeight);

    const weight = style.fontWeight ?? 400;
    const minFontSize = 22;
    const verticalPadding = Math.max(4, Math.floor(cssHeight * 0.015));
    const maxWidth = cssWidth * 0.995;
    const maxHeight = cssHeight - verticalPadding * 2;
    let fontSize = Math.max(style.fontSize ?? minFontSize, minFontSize);

    const buildFont = () => `${weight} ${fontSize}px ${style.fontFamily}`;

    const measure = () => {
      ctx.font = buildFont();
      return ctx.measureText(text);
    };

    let metrics = measure();
    const computeTextHeight = () => {
      const ascent = metrics.actualBoundingBoxAscent ?? 0;
      const descent = metrics.actualBoundingBoxDescent ?? 0;
      if (ascent + descent > 0) return ascent + descent;
      // Fallback when actual bounding box metrics are unavailable
      return fontSize * 1.05;
    };
    let textHeight = computeTextHeight();
    const fits = () => {
      const descenderReserve = Math.max(4, fontSize * 0.2);
      return metrics.width <= maxWidth && textHeight + descenderReserve <= maxHeight;
    };

    // Grow font until it no longer fits
    while (fits() && fontSize + 2 <= maxHeight) {
      fontSize += 2;
      metrics = measure();
      textHeight = computeTextHeight();
    }
    // If we overshot, shrink back down to fit
    while (!fits() && fontSize > minFontSize) {
      fontSize -= 1;
      metrics = measure();
      textHeight = computeTextHeight();
    }

    ctx.fillStyle = SIGNATURE_INK;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (!text) {
      setSignatureProvided(false);
      return;
    }
    const y = cssHeight / 2;
    ctx.fillText(text, cssWidth / 2, y);

    isDrawingRef.current = false;
    lastPointRef.current = null;
    setSignatureProvided(true);
  };

  React.useEffect(() => {
    if (signatureMode !== 'type') {
      return;
    }

    if (typedSignatureDebounceRef.current) {
      window.clearTimeout(typedSignatureDebounceRef.current);
    }

    typedSignatureDebounceRef.current = window.setTimeout(() => {
      applyTypedSignatureToCanvas();
    }, 300);

    return () => {
      if (typedSignatureDebounceRef.current) {
        window.clearTimeout(typedSignatureDebounceRef.current);
      }
    };
  }, [typedSignature, selectedSignatureStyle, signatureMode]);

  const currentDate = useMemo(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  }, [open]);

  const handleSubmit = async () => {
    if (!company?.id) {
      return;
    }

    try {
      const signatureDataUrl = getCanvasSignatureDataUrl();

      const payload: SignLoaPayload = {
        customer: formValues.customer,
        phoneNumbers: formValues.phoneNumber,
        address: formValues.address,
        phone: formValues.phone,
        signedOn: currentDate,
        signature: signatureDataUrl ?? '',
      };

      if (formValues.carrierName.trim()) {
        payload.carrierName = formValues.carrierName.trim();
      }
      if (formValues.carrierNumber.trim()) {
        payload.carrierNumber = formValues.carrierNumber.trim();
      }

      await addLoaApi(company.id, payload);
      toast.success('LOA signed successfully');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to sign LOA:', error);
      toast.error('Failed to sign LOA');
    }
  };

  const getCanvasSignatureDataUrl = () => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const maybeTrimVertical = () => {
      if (signatureMode !== 'type') return null;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      const width = canvas.width;
      const height = canvas.height;
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;

    const isRowWhite = (y: number) => {
        const rowStart = y * width * 4;
        const rowEnd = rowStart + width * 4;
        for (let i = rowStart; i < rowEnd; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
        const a = data[i + 3];
        if (a > 10 && (r < 245 || g < 245 || b < 245)) {
            return false;
          }
        }
        return true;
      };

      let top = 0;
      let bottom = height - 1;
      while (top < height && isRowWhite(top)) top += 1;
      while (bottom >= top && isRowWhite(bottom)) bottom -= 1;

      if (top === 0 && bottom === height - 1) {
        return null;
      }

      const padding = 10;
      const cropTop = Math.max(0, top - padding);
      const cropHeight = Math.max(1, Math.min(height - cropTop, bottom - top + 1 + padding * 2));

      const trimmedCanvas = document.createElement('canvas');
      trimmedCanvas.width = width;
      trimmedCanvas.height = cropHeight;
      const tctx = trimmedCanvas.getContext('2d');
      if (!tctx) return null;

      tctx.clearRect(0, 0, width, cropHeight);
      tctx.drawImage(canvas, 0, cropTop, width, cropHeight, 0, 0, width, cropHeight);
      return trimmedCanvas.toDataURL('image/png');
    };

    try {
      return maybeTrimVertical() ?? canvas.toDataURL('image/png');
    } catch (error) {
      console.error(error);
      return undefined;
    }
  };

  const handlePreview = async () => {
    if (!company?.id) {
      return;
    }

    try {
      setPreviewLoading(true);
      const signatureDataUrl = signatureProvided ? getCanvasSignatureDataUrl() : undefined;

      const payload = {
        customer: formValues.customer.trim() === '' ? undefined : formValues.customer,
        phoneNumbers: formValues.phoneNumber.trim() === '' ? undefined : formValues.phoneNumber,
        carrierName: formValues.carrierName.trim() === '' ? undefined : formValues.carrierName,
        carrierNumber: formValues.carrierNumber.trim() === '' ? undefined : formValues.carrierNumber,
        address: formValues.address.trim() === '' ? undefined : formValues.address,
        phone: formValues.phone.trim() === '' ? undefined : formValues.phone,
        signedOn: currentDate,
        signature: signatureDataUrl,
      };

      const response = await loaPreviewApi(company.id, payload);
      const blobUrl = URL.createObjectURL(response.data);

      // Create a mock CompanyDocument for the preview
      const mockDocument: CompanyDocument = {
        id: 'loa-preview',
        documentType: 'loa',
        fileName: 'LOA_Preview.pdf',
        url: blobUrl,
        dateCreated: new Date().toISOString(),
        dateUpdated: new Date().toISOString(),
      };

      setPreviewDocument(mockDocument);
      setPreviewModalOpen(true);
    } catch (error) {
      console.error('Failed to generate LOA preview:', error);
      toast.error('Failed to generate LOA preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Reset form when dialog opens
  React.useEffect(() => {
    if (!open) return;
    // Reset to empty state first
    setFormValues({
      customer: '',
      address: '',
      carrierName: '',
      carrierNumber: '',
      phoneNumber: '',
      phone: '',
    });
    setInitialFormValues({
      customer: '',
      address: '',
      carrierName: '',
      carrierNumber: '',
      phoneNumber: '',
      phone: '',
    });
    setTypedSignature('');
    setAgreeToSign(false);
    setSignatureProvided(false);
  }, [open]);

  // Prefill with known company data on open
  React.useEffect(() => {
    if (!open) return;
    if (company?.name) {
      const prefilledCustomer = company.name;
      setFormValues(prev => ({
        ...prev,
        customer: prev.customer || prefilledCustomer,
      }));
      setInitialFormValues(prev => ({
        ...prev,
        customer: prev.customer || prefilledCustomer,
      }));
    }
    // Address is not available on AdminCompany; left for future when source is defined
  }, [open, company]);

  return (
    <>
      <ConfirmationDialog />

      <BhiDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      title="Sign Letter of Authorization (LOA)"
      showCloseButton={false}
    >
      <DialogCloseButton onClick={handleClose}>
        <CloseIcon />
      </DialogCloseButton>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Please complete the fields below to authorize Telnyx as the Service Registrar for the following number
          and authorize the Service Registrar to route SMS and MMS on the following number.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} display="none">
            <Typography variant="overline" color="text.secondary">Customer Information</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Customer (Legal Name)'
              value={formValues.customer}
              onChange={handleChange('customer')}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Contact Phone Number"
              value={formValues.phone}
              onChange={handleChange('phone')}
              required
            />
          </Grid>          

          <Grid item xs={12} mt={1} display="none">
            <Divider />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Telephone Number"
              value={formValues.phoneNumber}
              onChange={handleChange('phoneNumber')}
              required
            />
          </Grid>

          <Grid item xs={12} mt={1} display="none">
            <Divider />
          </Grid>

          <Grid item xs={12} display="none">
            <Typography variant="overline" color="text.secondary">Carrier Information</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Carrier Account Name"
              value={formValues.carrierName}
              onChange={handleChange('carrierName')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Carrier Account Number"
              value={formValues.carrierNumber}
              onChange={handleChange('carrierNumber')}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address"
              placeholder="Street, Suite, City, State/Province, Zip/Postal Code"
              value={formValues.address}
              onChange={handleChange('address')}
              multiline
              minRows={2}
              maxRows={2}
              required
            />
          </Grid>

          <Grid item xs={12} mt={1} display="none">
            <Divider />
          </Grid>

          <Grid item xs={12} display="none">
            <Typography variant="overline" color="text.secondary">Signature</Typography>
          </Grid>
          <Grid item xs={12} mt={2}>
            <InsetPanel>
              <Box textAlign="center" mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Choose your preferred method to complete your signature
                </Typography>
              </Box>

              <BorderedTabs
                value={signatureMode}
                onChange={handleSignatureModeChange}
                variant="fullWidth"
                textColor="secondary"
                indicatorColor="secondary"
              >
                <Tab value="draw" icon={<BorderColorOutlined fontSize="small" />} iconPosition="start" label="Draw" />
                <Tab value="type" icon={<TextFieldsOutlined fontSize="small" />} iconPosition="start" label="Type" />
                <Tab value="upload" icon={<CloudUploadOutlined fontSize="small" />} iconPosition="start" label="Upload" />
              </BorderedTabs>

              <Box
                display="grid"
                gridTemplateRows={signatureMode === 'type' ? '44px 1fr' : '1fr'}
                rowGap={signatureMode === 'type' ? 1 : 0}
                mt={2}
                height={200}
              >
                {signatureMode === 'type' && (
                  <BorderedTabs
                    dense
                    value={selectedSignatureStyle}
                    onChange={handleSignatureStyleChange}
                    variant={isMobile ? 'scrollable' : 'standard'}
                    allowScrollButtonsMobile={isMobile}
                    scrollButtons={isMobile ? 'auto' : false}
                    textColor="secondary"
                    indicatorColor="secondary"
                    sx={{ width: '100%' }}
                  >
                    {SIGNATURE_STYLE_OPTIONS.map((style) => (
                      <Tab
                        key={style.id}
                        value={style.id}
                        label={
                          <Typography
                            variant="body2"
                            fontFamily={style.fontFamily}
                            fontWeight={style.fontWeight ?? 400}
                            fontSize={22}
                            lineHeight={1.25}
                            width="100%"
                            display="block"
                            textAlign="center"
                            whiteSpace="nowrap"
                          >
                            {style.label}
                          </Typography>
                        }
                      />
                    ))}
                  </BorderedTabs>
                )}
                <DashedFrame height="100%">
                  {signatureProvided && (
                    <Tooltip title="Clear signature">
                      <FloatingIconButton
                        aria-label="Clear signature"
                        size="small"
                        onClick={clearSignature}
                        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                      >
                        <DeleteOutline fontSize="small" />
                      </FloatingIconButton>
                    </Tooltip>
                  )}
                  {signatureMode === 'type' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: { xs: 2, md: 3 },
                      }}
                    >
                      <CenteredDisplayInput
                        fullWidth
                        value={typedSignature}
                        onChange={(e) => setTypedSignature(e.target.value)}
                        placeholder="Type your signature"
                        inputProps={{ 'aria-label': 'Typed signature input' }}
                        typeface={
                          hasTypedSignature
                            ? {
                                fontFamily: currentSignatureStyle.fontFamily,
                                fontWeight: currentSignatureStyle.fontWeight ?? 400,
                                fontSize: currentSignatureStyle.fontSize ?? 42,
                              }
                            : undefined
                        }
                      />
                    </Box>
                  )}
                  <canvas
                    ref={canvasRef}
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'block',
                      cursor: signatureMode === 'draw' ? 'crosshair' : 'default',
                      pointerEvents: signatureMode === 'draw' ? 'auto' : 'none',
                      opacity: signatureMode === 'type' ? 0 : 1,
                    }}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseLeave}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                  />
                  {!signatureProvided && signatureMode !== 'type' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        px: 3,
                        pointerEvents: signatureMode === 'upload' ? 'auto' : 'none',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        {signatureMode === 'draw' && 'Draw your signature using your mouse or touch device'}
                        {signatureMode === 'upload' && 'Upload a signature image to see it here'}
                      </Typography>
                      {signatureMode === 'upload' && (
                        <Button
                          variant="contained"
                          startIcon={<CloudUploadOutlined />}
                          onClick={openUploadDialog}
                          sx={{ pointerEvents: 'auto' }}
                        >
                          Upload
                        </Button>
                      )}
                    </Box>
                  )}
                </DashedFrame>

                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={uploadSignatureImage}
                />
              </Box>
            </InsetPanel>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreeToSign}
                  onChange={(e) => setAgreeToSign(e.target.checked)}
                />
              }
              label="I agree to sign this Letter of Authorization (LOA) electronically and confirm that the information provided is accurate."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={spreadDialogActionsSx}>
        <Button
          variant="contained"
          onClick={handleClose}
          color="error"
        >
          Close
        </Button>

        <Stack direction="row" gap={1}>
          <Button
            variant="outlined"
            onClick={handlePreview}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            Sign LOA
          </Button>
        </Stack>

      </DialogActions>

      {/* LOA Preview Modal */}
      <DocumentPreviewModal
        open={previewModalOpen && !previewLoading && !!previewDocument}
        onClose={() => {
          // Clean up blob URL when closing
          if (previewDocument?.url) {
            URL.revokeObjectURL(previewDocument.url);
          }
          setPreviewDocument(null);
          setPreviewModalOpen(false);
        }}
        document={previewDocument}
      />
    </BhiDialog>
    </>
  );
};

export default SignLoaDialog;
